import { Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

/** Substitut affiché à la place d'un montant en mode masqué. */
export const MASKED_AMOUNT = '$ ••••';

/**
 * Mode « montants masqués ».
 *
 * Bascule depuis l'œil du header de l'Accueil, il remplace **tous** les montants
 * de l'application par `•••` — solde héros, cartes événement, lignes de
 * contribution et de dépense comprises. Sert à consulter l'app en public sans
 * exposer la caisse.
 *
 * Persisté via Capacitor Preferences (web + natif) : le réglage survit à la
 * fermeture de l'app, sinon il ne protégerait rien au moment où c'est utile.
 */
/** Au-delà, l'application démarre sans attendre la préférence stockée. */
const PRIVACY_RESTORE_TIMEOUT_MS = 400;

@Injectable({ providedIn: 'root' })
export class PrivacyService {
  private static readonly KEY = 'te.amountsHidden';

  /** `true` quand les montants doivent être masqués. */
  readonly hidden = signal(false);

  /**
   * Résolue quand la préférence stockée a été lue.
   *
   * `Preferences.get()` est asynchrone : sans attendre, le PREMIER rendu se
   * faisait toujours avec `hidden() === false` et affichait les vrais montants
   * le temps de la lecture — exactement l'instant (ouvrir l'app en public) que
   * ce mode doit couvrir. `provideAppInitializer` attend cette promesse.
   */
  readonly ready = Promise.race([
    this.restore(),
    // Aucun app initializer n'a de délai maximal : si le pont Capacitor n'est pas
    // encore prêt et que `Preferences.get()` ne se règle jamais, le démarrage
    // n'aboutit pas et l'écran reste blanc, sans message ni issue. Au pire, le
    // masquage s'applique une image plus tard.
    new Promise<void>((resolve) => setTimeout(resolve, PRIVACY_RESTORE_TIMEOUT_MS)),
  ]).then(() => undefined);

  /** Inverse l'état et le persiste (best-effort). */
  async toggle(): Promise<void> {
    const next = !this.hidden();
    this.hidden.set(next);
    try {
      await Preferences.set({ key: PrivacyService.KEY, value: next ? '1' : '0' });
    } catch {
      /* persistance best-effort : la bascule reste appliquée pour la session */
    }
  }

  private async restore(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: PrivacyService.KEY });
      if (value === '1') this.hidden.set(true);
    } catch {
      /* pas de préférence stockée / stockage indisponible : montants visibles */
    }
  }
}
